import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../HighLevelComponents/Logo/Logo";
import { useFirebaseAuth } from "../../Hooks/FirebaseAuth";
import { auth } from "../../config/firebase-config";
import { YippityUserContext } from "../../Hooks/YippityUserContext";
import {
  FaBook,
  FaListAlt,
  FaCreditCard,
  FaSignOutAlt,
  FaBars,
  FaSave,
  FaLightbulb,
  FaCheckCircle,
  FaTools,
  FaHome,
} from "react-icons/fa";
import { BsFillLightningChargeFill, BsArrowRightShort } from "react-icons/bs";
import { useAuthenticatedFetch } from "../../Hooks/useFetch";
import config from "../../config";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import background from "../../images/colorful-background.png";

interface ToolItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  path: string;
  beta?: boolean;
}

const tools: ToolItem[] = [
  {
    id: "quiz",
    name: "Question Generator",
    icon: <FaBook size={16} />,
    path: "/tools/quiz",
  },
  {
    id: "mcq",
    name: "Multiple Choice",
    icon: <FaListAlt size={16} />,
    path: "/tools/mcq",
  },
  {
    id: "flashcards",
    name: "Flashcards",
    icon: <BsFillLightningChargeFill size={16} />,
    path: "/tools/flashcards",
  },
  {
    id: "automarker",
    name: "AI Automarker",
    icon: <FaCheckCircle size={16} />,
    path: "/tools/automarker",
    beta: true,
  },
];

interface SidebarLayoutProps {
  children: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeToolId, setActiveToolId] = useState<string>("");
  const [activePage, setActivePage] = useState<string>("");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { user, isSubscribed } = useContext(YippityUserContext);
  const { res, callAPI, isLoading } = useAuthenticatedFetch({
    url: config.API_URI + `/create-billing-portal-session`,
    method: "POST",
  });
  const firebaseAuth = useFirebaseAuth();
  const { logEvent } = useContext(GoogleAnalyticsContext);

  // Set active item based on current path
  useEffect(() => {
    const pathSegments = location.pathname.split("/");

    if (pathSegments.length >= 2) {
      // Set active section
      if (pathSegments[1] === "tools" && pathSegments.length >= 3) {
        setActiveToolId(pathSegments[2]);
        setActivePage("tools");
      } else if (pathSegments[1] === "quiz") {
        setActivePage("library");
      } else if (
        pathSegments[1] === "prompts" ||
        pathSegments[1] === "custom-tools"
      ) {
        setActivePage("custom-tools");
      } else {
        setActivePage(pathSegments[1]);
      }
    }
  }, [location]);

  useEffect(() => {
    if (res.statusCode === 200) {
      window.open(res.data.url, "_self");
    }
  }, [res]);

  const getBillingPortalSession = async () => {
    logEvent("Interaction", "Portal Session");
    callAPI();
  };

  const handleSignOut = () => {
    auth.signOut();
    localStorage.clear();
    navigate("/");
  };

  const handleToolClick = (path: string) => {
    navigate(path);
    setIsMobileSidebarOpen(false);
  };

  // Get remaining credits
  const getRemainingCredits = () => {
    return user?.quizzesCreated !== undefined
      ? Math.max(0, config.MONTHLY_ALLOWANCE - user.quizzesCreated)
      : 0;
  };

  // Glassmorphism styles as inline styles to ensure they work correctly
  const glassStyle = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  };

  const glassLighterStyle = {
    background: "rgba(255, 255, 255, 0.45)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
  };

  const glassDarkerStyle = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(7px)",
    WebkitBackdropFilter: "blur(7px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  };

  const activeItemStyle = {
    borderLeft: "2px solid #FFC60B",
    background: "rgba(255, 255, 255, 0.35)",
    backdropFilter: "blur(7px)",
    WebkitBackdropFilter: "blur(7px)",
  };

  return (
    <div
      className="flex min-h-screen w-full"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "250%",
      }}
    >
      {/* Mobile menu toggle */}
      <div className="fixed top-4 left-4 z-50 md:hidden">
        <button
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          style={{
            background: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
          className="p-2 rounded-full shadow-sm text-gray-700 hover:bg-white transition-colors"
        >
          <FaBars size={18} />
        </button>
      </div>

      {/* Sidebar - fixed on desktop, slide in/out on mobile */}
      <div
        className={`fixed h-full z-30 flex flex-col transition-all duration-300 ease-in-out w-64
          ${isMobileSidebarOpen ? "left-0" : "-left-64 md:left-0"}`}
        style={glassStyle}
      >
        {/* Logo section */}
        <div
          className="px-4 py-4 flex items-center justify-center border-b"
          style={{ borderColor: "rgba(255, 255, 255, 0.3)" }}
        >
          <Logo size="80px" />
        </div>

        {/* Scrollable content section */}
        <div className="flex-1 overflow-y-auto">
          {/* Home section */}
          <div className="mt-4">
            <div className="flex flex-col">
              <div
                className="flex items-center px-6 py-3 cursor-pointer transition-all duration-200"
                style={
                  activePage === "home"
                    ? activeItemStyle
                    : {
                        borderLeft: "2px solid transparent",
                        transition: "all 0.2s",
                      }
                }
                onClick={() => handleToolClick("/home")}
              >
                <div
                  className={
                    activePage === "home" ? "text-yellow-500" : "text-gray-500"
                  }
                >
                  <FaHome size={16} />
                </div>
                <div
                  className={`ml-3 text-sm ${
                    activePage === "home"
                      ? "font-medium text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Home
                </div>
              </div>
            </div>
          </div>

          {/* Tools section */}
          <div className="mt-2">
            <div className="px-6 mb-2">
              <span className="text-xs font-medium text-gray-700 tracking-wider">
                TOOLS
              </span>
            </div>
            <div className="flex flex-col">
              {tools.map((tool) => (
                <div
                  key={tool.id}
                  className="flex items-center px-6 py-3 cursor-pointer transition-all duration-200"
                  style={
                    activePage === "tools" && activeToolId === tool.id
                      ? activeItemStyle
                      : {
                          borderLeft: "2px solid transparent",
                          transition: "all 0.2s",
                        }
                  }
                  onClick={() => handleToolClick(tool.path)}
                >
                  <div
                    className={
                      activePage === "tools" && activeToolId === tool.id
                        ? "text-yellow-500"
                        : "text-gray-500"
                    }
                  >
                    {tool.icon}
                  </div>
                  <div
                    className={`ml-3 text-sm ${
                      activePage === "tools" && activeToolId === tool.id
                        ? "font-medium text-gray-900"
                        : "text-gray-600"
                    }`}
                  >
                    {tool.name}
                    {tool.beta && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                        Beta
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Tools section (formerly AI Prompts) */}
          <div className="mt-4">
            <div className="px-6 mb-2">
              <span className="text-xs font-medium text-gray-700 tracking-wider">
                CUSTOM TOOLS
              </span>
            </div>
            <div className="flex flex-col">
              <div
                className="flex items-center px-6 py-3 cursor-pointer transition-all duration-200"
                style={
                  activePage === "custom-tools"
                    ? activeItemStyle
                    : {
                        borderLeft: "2px solid transparent",
                        transition: "all 0.2s",
                      }
                }
                onClick={() => handleToolClick("/custom-tools")}
              >
                <div
                  className={
                    activePage === "custom-tools"
                      ? "text-yellow-500"
                      : "text-gray-500"
                  }
                >
                  <FaTools size={16} />
                </div>
                <div
                  className={`ml-3 text-sm ${
                    activePage === "custom-tools"
                      ? "font-medium text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  My Tools
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                    🔥 Hot
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Library section */}
          <div className="mt-4">
            <div className="px-6 mb-2">
              <span className="text-xs font-medium text-gray-700 tracking-wider">
                LIBRARY
              </span>
            </div>
            <div className="flex flex-col">
              <div
                className="flex items-center px-6 py-3 cursor-pointer transition-all duration-200"
                style={
                  activePage === "library"
                    ? activeItemStyle
                    : {
                        borderLeft: "2px solid transparent",
                        transition: "all 0.2s",
                      }
                }
                onClick={() => handleToolClick("/quiz")}
              >
                <div
                  className={
                    activePage === "library"
                      ? "text-yellow-500"
                      : "text-gray-500"
                  }
                >
                  <FaSave size={16} />
                </div>
                <div
                  className={`ml-3 text-sm ${
                    activePage === "library"
                      ? "font-medium text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Saved Quizzes
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom section */}
        <div className="flex flex-col">
          {/* Subscription card */}
          {!isSubscribed && (
            <div className="mx-4 mb-4 p-3 rounded-lg" style={glassLighterStyle}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">
                  Free Credits
                </span>
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full text-yellow-800"
                  style={{ background: "rgba(254, 240, 138, 0.7)" }}
                >
                  {getRemainingCredits()} left
                </span>
              </div>
              <button
                onClick={() => navigate("/pricing")}
                className="w-full mt-1 py-1.5 px-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-sm text-gray-800 font-medium rounded flex items-center justify-center hover:shadow-sm transition-all"
              >
                Upgrade <BsArrowRightShort size={18} />
              </button>
            </div>
          )}

          {/* Footer section */}
          <div className="px-6 py-4 border-t" style={glassDarkerStyle}>
            {/* Show billing portal link for subscribed users */}
            {isSubscribed && (
              <div
                className="flex items-center py-2 cursor-pointer text-gray-600 hover:text-gray-900 transition-colors"
                onClick={getBillingPortalSession}
              >
                <FaCreditCard size={16} className="text-gray-500" />
                <span className="ml-3 text-sm">Manage Subscription</span>
              </div>
            )}
            <div
              className="flex items-center py-2 cursor-pointer text-gray-600 hover:text-gray-900 transition-colors"
              onClick={handleSignOut}
            >
              <FaSignOutAlt size={16} className="text-gray-500" />
              <span className="ml-3 text-sm">Sign Out</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 md:ml-64 ml-0 p-5 min-h-screen">
        <div className="rounded-lg p-6 h-full">{children}</div>
      </div>
    </div>
  );
};

export default SidebarLayout;
